import { type PropsWithChildren, useEffect } from "react";
import { getAllMappings } from "@oneapp/data";
import {
  findCountryMappingByIsdCode,
  getExperienceIsdCode,
  resolveCountryExperience,
} from "@repo/utils";
import { useAuthStore, type CountryDetails } from "../../store/useAuthStore";

// Login restores saved account data silently. Timezone selection belongs to signup.
export function StudentTimezoneBootstrap({ children }: PropsWithChildren) {
  const token = useAuthStore((state) => state.token);
  const profilesData = useAuthStore((state) => state.profilesData);
  const countryDetails = useAuthStore((state) => state.countryDetails);
  const setCountryDetails = useAuthStore((state) => state.setCountryDetails);
  useEffect(() => {
    if (!token || !profilesData) return;
    const userDetails =
      profilesData.unified_account_details?.user_account_details;
    if (!userDetails) return;
    const experienceIsd = getExperienceIsdCode(userDetails.isd_code);
    // Repair persisted country data according to the shared local switch.
    if (
      countryDetails &&
      findCountryMappingByIsdCode({ items: [countryDetails] }, experienceIsd)
    )
      return;
    const requestState = { cancelled: false };
    const restoreTimezone = async () => {
      try {
        const mappings = await getAllMappings(
          { resource_type: "country" },
          { clientName: "masterData" },
        );
        if (requestState.cancelled) return;
        const timezoneId =
          typeof userDetails.timezone_id === "number"
            ? userDetails.timezone_id
            : null;
        const { country, selectedTimezoneId } = resolveCountryExperience(
          mappings,
          userDetails.isd_code,
          timezoneId,
        );
        if (country)
          setCountryDetails(
            country as unknown as CountryDetails,
            selectedTimezoneId,
          );
        // Missing or ambiguous timezones must not interrupt login.
      } catch (error) {
        console.warn("[TimezoneBootstrap] Failed to restore timezone", error);
      }
    };
    void restoreTimezone();
    return () => {
      requestState.cancelled = true;
    };
  }, [countryDetails, profilesData, setCountryDetails, token]);
  return <div>{children}</div>;
}
